"use client";

import { colorPrimaryOnPrimaryContainer, colorPrimaryPrimaryContainer, colorSurfaceSurfaceVar } from "@/app/(styles)/variables";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";
import Drawer from "@mui/material/Drawer";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useQuestionsStore } from "@/store/questionsStore";
import { QuesSheetCheckboxButtons } from "./onboardingFlow/QuesSheetCheckboxButtons";
import { QuesSheetFooter } from "./onboardingFlow/QuesSheetFooter";
import { QuesSheetRadioButtons } from "./onboardingFlow/QuesSheetRadioButtons";
import questions from "./onboardingFlow/questions.json";
import { getStepInfoUsingPaths } from "./utils/onboardingQuestionUtils";
import { useEffect } from "react";
import { pushGtmEvent } from "../../../lib/utils/gtmEventHandler";
import { useDpStore } from "@/store/dpStore";
export default function OnboardingFlowNew() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const {
    setIsEnquiryModalOpen
  } = useDpStore();
  const currentQuestionId = searchParams.get("questionId") ?? questions?.startScreen;
  const currentQuestionData = questions?.screens?.[currentQuestionId];
  const {
    storeAnswers,
    setStoreAnswers
  } = useQuestionsStore();
  const isFirstScreen = currentQuestionId === questions?.startScreen;
  const isLastScreen = currentQuestionData?.navigation?.next === "end";
  const isQuestionSheetOpen = searchParams.get("isQuestionSheetOpen") === "true";
  const handleCloseQuestionSheet = () => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete("isQuestionSheetOpen");
    params.delete("questionId");
    router.replace(`${pathname}?${params.toString()}`, {
      scroll: false
    });
  };
  const handleQuestionSheetSubmit = () => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete("isQuestionSheetOpen");
    if (pathname.includes("digital-profile")) {
      router.replace(`${pathname}?${params.toString()}`, {
        scroll: false
      });
      setIsEnquiryModalOpen(true);
      return;
    }
    router.push("/counting-loader");
  };
  const eventNameMapping = {
    q1: "question_property_type_viewed",
    q2: "question_project_purpose_viewed",
    q3_full: "question_sqft_viewed",
    q3_commercial: "question_sqft_viewed",
    q3_partial: "question_rooms_viewed",
    q4: "question_sqft_viewed",
    q5: "question_timeline_viewed",
    q6: "question_budget_viewed"
  };
  const eventActionMapping = {
    q1: "question_property_type_action_taken",
    q2: "question_project_purpose_action_taken",
    q3_full: "question_sqft_action_taken",
    q3_commercial: "question_sqft_action_taken",
    q3_partial: "question_rooms_action_taken",
    q4: "question_sqft_action_taken",
    q5: "question_timeline_action_taken",
    q6: "question_budget_action_taken"
  };
  const handleNext = ({
    answerValue = null
  }) => {
    const params = new URLSearchParams(searchParams.toString());
    const currentQuestionId = params.get("questionId");
    const valueToUse = answerValue || storeAnswers[currentQuestionId]?.value || JSON.stringify(storeAnswers[currentQuestionId]?.values);
    pushGtmEvent({
      event_name: eventActionMapping[currentQuestionId],
      event_action: "next_button_clicked",
      event_value: valueToUse
    });
    if (currentQuestionData?.navigation?.type === "conditional") {
      const nextQuestionId = currentQuestionData.navigation.conditions.find(condition => condition.when === valueToUse)?.next;
      params.set("questionId", nextQuestionId);
      router.push(`${pathname}?${params.toString()}`, {
        scroll: false
      });
      return;
    }
    const nextQuestionId = currentQuestionData?.navigation?.next;
    params.set("questionId", nextQuestionId);
    router.push(`${pathname}?${params.toString()}`, {
      scroll: false
    });
  };
  const handlePrevious = () => {
    const params = new URLSearchParams(searchParams.toString());
    const previousQuestionId = currentQuestionData?.navigation?.previous;
    pushGtmEvent({
      event_name: eventActionMapping[currentQuestionId],
      event_action: "previous_button_clicked",
      event_value: `${currentQuestionData?.question}`
    });
    params.set("questionId", previousQuestionId);
    router.replace(`${pathname}?${params.toString()}`, {
      scroll: false
    });
  };
  const {
    currentStep,
    totalSteps
  } = getStepInfoUsingPaths({
    screens: questions?.screens || {},
    answers: storeAnswers,
    startId: questions?.startScreen,
    currentQuestionId: currentQuestionId
  });
  useEffect(() => {
    if (isQuestionSheetOpen) {
      pushGtmEvent({
        event_name: eventNameMapping[currentQuestionId],
        event_value: currentQuestionData?.question
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestionId, isQuestionSheetOpen]);
  return <Drawer anchor="bottom" open={isQuestionSheetOpen} onClose={handleCloseQuestionSheet} PaperProps={{
    sx: {
      height: "100%",
      width: {
        xs: "100%",
        md: "480px"
      },
      maxWidth: {
        xs: "100%",
        md: "480px"
      },
      backgroundColor: colorSurfaceSurfaceVar,
      borderRadius: 0,
      maxHeight: "100vh",
      overflow: "hidden",
      margin: {
        xs: 0,
        md: "0 auto"
      },
      boxShadow: "none",
      border: "1px solid var(--colorSecondarySecondaryContainer)"
    }
  }} ModalProps={{
    keepMounted: false
  }} slotProps={{
    backdrop: {
      sx: {
        backgroundColor: colorSurfaceSurfaceVar
      }
    }
  }}>
      <div className="flex flex-col h-full w-full">
        {/* Header with Close Button */}
        <div className="flex items-center p-4 pt-5 md:p-6 md:pt-7">
          <IconButton onClick={handleCloseQuestionSheet} sx={{
          padding: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: colorPrimaryOnPrimaryContainer
        }}>
            <CloseIcon />
          </IconButton>
        </div>

        <div className="flex flex-col flex-1 px-4 pt-4 md:px-6 md:pt-6 overflow-y-auto justify-start">
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <span className="m3-title-medium text-[--textLinkOnColorLink]">
              {currentStep} of {totalSteps}
            </span>
            <AssignmentOutlinedIcon style={{
            color: colorPrimaryPrimaryContainer,
            fontSize: "36px"
          }} />
          </div>
          <div className="m3-headline-small text-[--textPrimaryPrimary] text-left mb-8">
            {currentQuestionData?.question}
          </div>

          {currentQuestionData?.type === "radio" ? <QuesSheetRadioButtons currentQuestionData={currentQuestionData} setAnswers={setStoreAnswers} answers={storeAnswers} handleQuestionSheetSubmit={handleQuestionSheetSubmit} handleNext={handleNext} /> : currentQuestionData?.type === "checkbox" ? <QuesSheetCheckboxButtons currentQuestionData={currentQuestionData} setAnswers={setStoreAnswers} answers={storeAnswers} /> : null}
        </div>

        {/* Footer with Buttons */}
        <QuesSheetFooter handleNext={handleNext} handlePrevious={handlePrevious} handleSubmit={handleQuestionSheetSubmit} answers={storeAnswers} currentQuestionData={currentQuestionData} isFirstScreen={isFirstScreen} isLastScreen={isLastScreen} />
      </div>
    </Drawer>;
}